"use client";

import React, { useState } from "react";
import Link from "next/link";
import {
    Calendar, User, Clock, ArrowRight, Search, Tag,
    ChevronRight, Eye, FileText, Compass
} from "lucide-react";

export default function BlogClient({ posts, categories, settings }: { posts: any[], categories: any[], settings: any }) {
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("all");

    // Canlı Arama ve Kategori Filtreleme
    const filteredPosts = posts.filter(post => {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
            post.title.toLowerCase().includes(searchLower) ||
            (post.excerpt || "").toLowerCase().includes(searchLower) ||
            (post.content || "").toLowerCase().includes(searchLower);

        const matchesCategory = selectedCategory === "all" || post.categoryId === selectedCategory;

        return matchesSearch && matchesCategory;
    });

    const featuredPost = filteredPosts[0];
    const regularPosts = filteredPosts.slice(1);

    // Popüler Yazılar (Okunma sayısına göre ilk 3)
    const popularPosts = [...posts].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 3);

    // Yardımcı Fonksiyonlar
    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    const calculateReadTime = (content: string) => {
        if (!content) return "3 dk";
        const words = content.trim().split(/\s+/).length;
        return `${Math.ceil(words / 200)} dk`;
    };

    const getAuthorName = (author: any) => {
        if (!author) return settings.siteName || "Uzman Ekibimiz";
        return `${author.name} ${author.surname || ''}`;
    };

    return (
        <div className="bg-white min-h-screen">

            {/* HERO SECTION */}
            <section className="relative py-16 md:py-24 overflow-hidden" style={{ background: 'linear-gradient(to bottom right, color-mix(in srgb, var(--color-primary), transparent 95%), white)' }}>
                <div className="absolute top-10 left-10 w-64 h-64 rounded-full blur-3xl opacity-20" style={{ backgroundColor: 'var(--color-primary)' }}></div>
                <div className="absolute bottom-10 right-10 w-80 h-80 rounded-full blur-3xl opacity-10" style={{ backgroundColor: 'var(--color-primary)' }}></div>

                <div className="container mx-auto max-w-7xl relative z-10 px-4 sm:px-6">
                    <div className="text-center max-w-3xl mx-auto">
                        <div className="inline-flex items-center px-4 py-2 rounded-full text-xs font-black tracking-widest mb-6 uppercase shadow-sm"
                            style={{ backgroundColor: 'color-mix(in srgb, var(--color-primary), transparent 90%)', color: 'var(--color-primary)' }}>
                            <Compass className="w-4 h-4 mr-2" /> Bilgi Merkezi
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-slate-900 mb-6 tracking-tight">
                            En Güncel <span style={{ color: 'var(--color-primary)' }}>İçerikler</span>
                        </h1>
                        <p className="text-lg md:text-xl text-slate-600 mb-10 font-medium">
                            Sigorta dünyasındaki yenilikler, uzman tavsiyeleri ve hayatınızı kolaylaştıracak rehberler.
                        </p>

                        {/* Arama Kutusu */}
                        <div className="relative max-w-2xl mx-auto group">
                            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-6 h-6 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                            <input
                                type="text"
                                placeholder="Yazılarda veya konularda ara..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-14 pr-6 py-5 bg-white border-2 border-slate-100 text-slate-800 rounded-2xl focus:outline-none focus:border-transparent text-lg shadow-lg shadow-slate-200/50 transition-all font-medium"
                                style={{ boxShadow: searchTerm ? '0 10px 25px -5px color-mix(in srgb, var(--color-primary), transparent 80%)' : '' }}
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* İÇERİK BÖLÜMÜ */}
            <section className="py-16 md:py-20 px-4 sm:px-6 bg-white">
                <div className="container mx-auto max-w-7xl">
                    <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">

                        {/* SOL: ANA İÇERİK (LİSTE) */}
                        <div className="lg:w-2/3">

                            {/* Kategori Filtreleri */}
                            {categories.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-10 pb-6 border-b border-slate-100">
                                    <button
                                        onClick={() => setSelectedCategory("all")}
                                        className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 ${selectedCategory === "all" ? "text-white shadow-md shadow-blue-500/20" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                                        style={selectedCategory === "all" ? { backgroundColor: 'var(--color-primary)' } : {}}
                                    >
                                        Tümü ({posts.length})
                                    </button>
                                    {categories.map((cat) => (
                                        <button
                                            key={cat.id}
                                            onClick={() => setSelectedCategory(cat.id)}
                                            className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 ${selectedCategory === cat.id ? "text-white shadow-md" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                                            style={selectedCategory === cat.id ? { backgroundColor: 'var(--color-primary)' } : {}}
                                        >
                                            {cat.name} ({cat._count?.blogs || 0})
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Öne Çıkan Yazı */}
                            {featuredPost && (
                                <div className="mb-12 group cursor-pointer">
                                    <div className="bg-white rounded-[2.5rem] shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl">
                                        <div className="relative h-72 md:h-96 w-full overflow-hidden">
                                            <img
                                                src={featuredPost.featuredImage || "/assets/images/placeholder.png"}
                                                alt={featuredPost.title}
                                                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                            />
                                            <div className="absolute inset-0 bg-linear-to-t from-slate-900/80 via-slate-900/20 to-transparent"></div>
                                            <div className="absolute bottom-8 left-8 right-8 text-white">
                                                <span className="inline-flex items-center px-4 py-1.5 rounded-xl text-xs font-bold mb-4 backdrop-blur-md bg-white/20 border border-white/30 uppercase tracking-widest">
                                                    {featuredPost.category?.name || "Genel"}
                                                </span>
                                                <h2 className="text-3xl md:text-4xl font-black mb-3 leading-tight tracking-tight">
                                                    {featuredPost.title}
                                                </h2>
                                                <div className="flex items-center text-sm text-slate-200 font-medium">
                                                    <User className="w-4 h-4 mr-1.5" /> {getAuthorName(featuredPost.author)}
                                                    <span className="mx-3 opacity-50">•</span>
                                                    <Calendar className="w-4 h-4 mr-1.5" /> {formatDate(featuredPost.publishedAt || featuredPost.createdAt)}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="p-8 md:p-10 flex items-center justify-between">
                                            <p className="text-slate-600 line-clamp-2 max-w-xl font-medium text-lg">
                                                {featuredPost.excerpt || featuredPost.title}
                                            </p>
                                            <Link href={`/blog/${featuredPost.slug}`} className="hidden md:flex items-center justify-center w-14 h-14 rounded-full bg-slate-100 text-slate-900 group-hover:bg-blue-600 group-hover:text-white transition-colors shrink-0">
                                                <ArrowRight className="w-6 h-6" />
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Diğer Yazılar Grid */}
                            {regularPosts.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    {regularPosts.map((post) => (
                                        <Link href={`/blog/${post.slug}`} key={post.id} className="group flex flex-col h-full bg-white rounded-3xl shadow-sm hover:shadow-xl border border-slate-100 overflow-hidden transition-all duration-300 hover:-translate-y-1">
                                            <div className="relative h-56 w-full overflow-hidden">
                                                <img
                                                    src={post.featuredImage || "/assets/images/placeholder.png"}
                                                    alt={post.title}
                                                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                                />
                                                <div className="absolute top-4 left-4 backdrop-blur-md bg-white/90 px-3 py-1.5 rounded-lg text-xs font-bold text-slate-800 shadow-sm">
                                                    {post.category?.name || "Genel"}
                                                </div>
                                            </div>
                                            <div className="p-6 flex flex-col flex-1">
                                                <h3 className="text-xl font-black text-slate-900 mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors leading-snug">
                                                    {post.title}
                                                </h3>
                                                <p className="text-slate-500 mb-6 line-clamp-2 font-medium text-sm flex-1">
                                                    {post.excerpt || "Devamını okumak için tıklayın..."}
                                                </p>
                                                <div className="flex items-center justify-between text-xs font-bold text-slate-400 mt-auto pt-4 border-t border-slate-100">
                                                    <span className="flex items-center"><Calendar className="w-3.5 h-3.5 mr-1.5" /> {formatDate(post.publishedAt || post.createdAt)}</span>
                                                    <span className="flex items-center"><Clock className="w-3.5 h-3.5 mr-1.5" /> {post.readTime || calculateReadTime(post.content)}</span>
                                                </div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            ) : (
                                /* Sonuç Bulunamadı */
                                <div className="text-center py-20 bg-slate-50 rounded-3xl border border-slate-100">
                                    <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
                                        {searchTerm ? <Search className="w-8 h-8 text-slate-400" /> : <FileText className="w-8 h-8 text-slate-400" />}
                                    </div>
                                    <h3 className="text-2xl font-black text-slate-900 mb-2">
                                        {searchTerm ? "Sonuç Bulunamadı" : "Henüz İçerik Yok"}
                                    </h3>
                                    <p className="text-slate-500 font-medium max-w-sm mx-auto">
                                        {searchTerm ? "Aradığınız kriterlere uygun bir yazı bulamadık. Başka kelimelerle deneyebilirsiniz." : "Çok yakında harika içeriklerle karşınızda olacağız!"}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* SAĞ: SİDEBAR */}
                        <div className="lg:w-1/3">
                            <div className="space-y-8 sticky top-32">

                                {/* Popüler Yazılar */}
                                {popularPosts.length > 0 && (
                                    <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8">
                                        <h3 className="text-xl font-black text-slate-900 mb-6 flex items-center border-b border-slate-100 pb-4">
                                            <Eye className="w-5 h-5 mr-2" style={{ color: 'var(--color-primary)' }} /> En Çok Okunanlar
                                        </h3>
                                        <div className="space-y-5">
                                            {popularPosts.map((post, i) => (
                                                <Link key={post.id} href={`/blog/${post.slug}`} className="flex items-center gap-4 group">
                                                    <div className="font-black text-4xl text-slate-100 group-hover:text-blue-100 transition-colors w-8 text-center shrink-0">
                                                        {i + 1}
                                                    </div>
                                                    <div>
                                                        <h4 className="text-sm font-bold text-slate-800 line-clamp-2 group-hover:text-blue-600 transition-colors leading-tight mb-1">
                                                            {post.title}
                                                        </h4>
                                                        <p className="text-xs font-semibold text-slate-400">
                                                            {post.views?.toLocaleString()} Görüntülenme
                                                        </p>
                                                    </div>
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Kategoriler Listesi */}
                                {categories.length > 0 && (
                                    <div className="bg-slate-50 rounded-3xl border border-slate-100 p-8">
                                        <h3 className="text-xl font-black text-slate-900 mb-6 flex items-center border-b border-slate-200 pb-4">
                                            <Tag className="w-5 h-5 mr-2 text-slate-600" /> Kategoriler
                                        </h3>
                                        <div className="space-y-2">
                                            {categories.map((cat) => (
                                                <button
                                                    key={cat.id}
                                                    onClick={() => { setSelectedCategory(cat.id); window.scrollTo({ top: 300, behavior: 'smooth' }); }}
                                                    className="flex items-center justify-between w-full p-3 rounded-xl text-left transition-all hover:bg-white hover:shadow-sm group"
                                                >
                                                    <span className="font-bold text-slate-700 group-hover:text-blue-600 transition-colors">{cat.name}</span>
                                                    <span className="text-xs font-black text-slate-400 bg-slate-200/50 px-2.5 py-1 rounded-lg">
                                                        {cat._count?.blogs || 0}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}